/**
 * AutoReloadCard — "Auto-reload credits" for PAID users on the upgrade page.
 *
 * Toggle + a compartment that opens when enabled: two dropdowns ("when my
 * balance falls below X credits, top up with Y credits"), the price of the
 * chosen pack at the standard $2/10-credit rate, and the default card the
 * charge goes to. Settings persist immediately via v2-auto-reload `set`; the
 * actual top-up is triggered server-side by the `check` action (nudged from
 * userStore.refreshUser whenever a fresh balance lands under the threshold).
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, CreditCard, AlertTriangle, Loader2 } from 'lucide-react';
import { apiService } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import { Toggle } from '@/v2/components/Toggle';
import { toast } from '@/v2/toast';
import type { AutoReloadSettings } from '@/api/v2Types';

const DEFAULT_THRESHOLDS = [5, 10, 25, 50, 100];
const DEFAULT_AMOUNTS = [50, 100, 200, 500, 1000];

export function AutoReloadCard() {
  const { t } = useTranslation('v2');
  const userId = useUserStore((s) => s.userId);
  const refreshUser = useUserStore((s) => s.refreshUser);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [amount, setAmount] = useState(200);
  const [settings, setSettings] = useState<AutoReloadSettings | null>(null);
  // Serializes saves: a toggle spam or fast dropdown changes must apply in
  // order, and the LAST state wins.
  const saveChain = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    let cancelled = false;
    apiService.autoReloadGet(userId)
      .then((data) => {
        if (cancelled) return;
        setSettings(data);
        setEnabled(data.enabled);
        setThreshold(data.threshold);
        setAmount(data.amount);
      })
      .catch(() => { /* endpoint unavailable — card still renders with defaults */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  const thresholds = settings?.allowedThresholds?.length ? settings.allowedThresholds : DEFAULT_THRESHOLDS;
  const amounts = settings?.allowedAmounts?.length ? settings.allowedAmounts : DEFAULT_AMOUNTS;
  const pricePerPack = (settings?.pricePerPackCents ?? 200) / 100;
  const creditsPerPack = settings?.creditsPerPack ?? 10;
  const packPrice = (amount / creditsPerPack) * pricePerPack;
  const card = settings?.card;
  const wasAutoDisabled = !enabled && settings?.disabledReason === 'payment_failed';

  const save = (next: { enabled: boolean; threshold: number; amount: number }, revert: () => void) => {
    if (!userId) return;
    setSaving(true);
    saveChain.current = saveChain.current
      .then(() => apiService.autoReloadSet(userId, next))
      .then(() => {
        setSettings((s) => (s ? { ...s, ...next, disabledReason: '' } : s));
        // Keep the store's nudge gate in sync without a full round-trip.
        void refreshUser();
      })
      .catch((err: unknown) => {
        revert();
        const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(code === 'no_card_on_file'
          ? t('upgrade.autoReloadNoCardError')
          : t('upgrade.autoReloadSaveFailed'));
      })
      .finally(() => setSaving(false));
  };

  const onToggle = (next: boolean) => {
    const prev = enabled;
    setEnabled(next);
    save({ enabled: next, threshold, amount }, () => setEnabled(prev));
  };
  const onThreshold = (next: number) => {
    const prev = threshold;
    setThreshold(next);
    save({ enabled, threshold: next, amount }, () => setThreshold(prev));
  };
  const onAmount = (next: number) => {
    const prev = amount;
    setAmount(next);
    save({ enabled, threshold, amount: next }, () => setAmount(prev));
  };

  const selectCls = 'rounded-lg bg-surface border border-glass-border px-2.5 py-1.5 text-sm font-semibold text-text-primary outline-none focus:border-primary/50 transition-colors cursor-pointer';

  return (
    <div className="p-6 rounded-2xl bg-surface border border-glass-border mb-4 text-left">
      <div className="flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <RefreshCw className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-text-primary flex items-center gap-2">
            {t('upgrade.autoReloadTitle')}
            {(loading || saving) && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-tertiary" />}
          </p>
          <p className="text-xs text-text-secondary mt-0.5">{t('upgrade.autoReloadDesc')}</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label={t('upgrade.autoReloadTitle')} />
      </div>

      {/* Auto-disabled after repeated card failures — tell them why it's off. */}
      {wasAutoDisabled && (
        <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3.5 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <p className="text-xs text-amber-200/90 leading-relaxed">{t('upgrade.autoReloadDisabledFailed')}</p>
        </div>
      )}

      {/* The compartment — opens when auto-reload is on. */}
      {enabled && (
        <div className="mt-5 rounded-xl bg-surface-light p-4 animate-fade-in space-y-3.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-text-secondary">
            <span>{t('upgrade.autoReloadWhenBelow')}</span>
            <select value={threshold} onChange={(e) => onThreshold(Number(e.target.value))} className={selectCls}>
              {thresholds.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <span>{t('upgrade.autoReloadCreditsWord')}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-text-secondary">
            <span>{t('upgrade.autoReloadTopUpWith')}</span>
            <select value={amount} onChange={(e) => onAmount(Number(e.target.value))} className={selectCls}>
              {amounts.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
            <span>{t('upgrade.autoReloadCreditsWord')}</span>
            <span className="ml-auto text-sm font-bold text-primary">${packPrice.toFixed(2)}</span>
          </div>

          <div className="border-t border-glass-border pt-3.5 flex items-center gap-2.5">
            <CreditCard className="w-4 h-4 shrink-0 text-text-tertiary" />
            {card?.hasCard ? (
              <p className="text-xs text-text-tertiary">
                {t('upgrade.autoReloadChargeNote', {
                  card: `${(card.brand || 'card').replace(/^./, (c) => c.toUpperCase())} •••• ${card.last4 || '????'}`,
                })}
              </p>
            ) : (
              <p className="text-xs text-amber-400">{t('upgrade.autoReloadNoCard')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
